import { apiAxios } from '../utils/axios';

// =====================
// TypeScript Interfaces
// =====================

export interface DailyTransaction {
  date: string;  // "2024-12-29"
  txn: number;
}

export interface MonthlyDeposit {
  date: string;  // "2024-12"
  usd_sum: number;
}

export interface MonthlyUserStats {
  reg_date: string;  // "2024-12"
  new_users: number;
  tx_count: number;
  usd_count: number;
}

export interface DailyUserStats {
  date: string;
  active_users?: number;
  // Add additional fields as needed
}

export interface UsersTotal {
  count: number;
}

export interface Mirror {
  mirror_id: number;
  bot_id: number;
  bot_username: string;
  status: number;
  created_at: string;
  total_users: number;
  daily_active_users: number;
  weekly_active_users: number;
  monthly_active_users: number;
  today_active_users: number;
  last_user_activity: string;
}

export interface MirrorStatistics {
  mirrors: Mirror[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    has_more: boolean;
  };
  order_by: string;
}

export interface ApiResponse<T> {
  success?: boolean;
  status?: string;  // API returns 'success' or 'error'
  data?: T;
  error?: string;
}

// ---------------------
// User/subscription management
// ---------------------

export type UserLookupField = 'tg_username' | 'email' | 'user_id' | 'username';

export interface UserMatch {
  user_id: number;
  email: string | null;
  username: string | null;
  tg_username: string | null;
  telegram_user_id: number | null;
}

export interface UserLookupResult {
  matches: UserMatch[];
}

export interface UserPlan {
  id: number;
  product_id: number;
  product_name: string | null;
  requests_left: number;
  date_start: string | null;
  date_end: string | null;
  is_active: boolean;
  is_fallback: boolean;
  status: 'active' | 'expired' | 'fallback';
}

export interface UserTelegramLink {
  telegram_user_id: number;
  username: string | null;
}

export interface UserState {
  user_id: number;
  username: string | null;
  email: string | null;
  reg_date?: string | null;
  plans: UserPlan[];
  total_remaining: number;
  balance: number;
  telegram: UserTelegramLink | null;
}

export interface CreditRequestsResult {
  total_remaining: number;
}

export type ExtendMode = 'delta' | 'absolute';

export interface ExtendSubscriptionPayload {
  user_id: number;
  mode: ExtendMode;
  days?: number;
  months?: number;
  date_end?: string; // YYYY-MM-DD
  plan_id?: number; // target a specific plan; omit to use the prioritized one
}

export interface Product {
  id: number;
  product_name_en: string | null;
  product_name_ru: string | null;
  add_requests: number | string | null;
  price_usd: number | null;
  tg_flag: number | null;
  web_flag: number | null;
}

export interface ProductsResult {
  products: Product[];
}

// =====================
// Admin API Functions
// =====================

/**
 * GET /admin/dayly_transactions
 * Get daily purchase transactions (BUY)
 */
export const getDailyTransactions = async (
  token: string,
  days: number = 7,
  offset: number = 0
): Promise<ApiResponse<DailyTransaction[]>> => {
  try {
    const response = await apiAxios.get('/admin/dayly_transactions', {
      params: { days, offset },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // Normalize API response: status -> success
    return {
      success: response.data.status === 'success',
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to fetch daily transactions',
    };
  }
};

/**
 * GET /admin/monthly_deposits
 * Monthly deposit statistics (top-ups)
 */
export const getMonthlyDeposits = async (
  token: string,
  months: number = 11,
  offset: number = 0
): Promise<ApiResponse<MonthlyDeposit[]>> => {
  try {
    const response = await apiAxios.get('/admin/monthly_deposits', {
      params: { months, offset },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // Normalize API response: status -> success
    return {
      success: response.data.status === 'success',
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to fetch monthly deposits',
    };
  }
};

/**
 * GET /admin/monthly_new_users
 * New users by month
 */
export const getMonthlyNewUsers = async (
  token: string,
  months: number = 11,
  offset: number = 0
): Promise<ApiResponse<MonthlyUserStats[]>> => {
  try {
    const response = await apiAxios.get('/admin/monthly_new_users', {
      params: { months, offset },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // Normalize API response: status -> success
    return {
      success: response.data.status === 'success',
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to fetch monthly new users',
    };
  }
};

/**
 * GET /admin/dayly_user_stats
 * Daily user activity statistics
 */
export const getDailyUserStats = async (
  token: string,
  days: number = 7,
  offset: number = 0
): Promise<ApiResponse<DailyUserStats[]>> => {
  try {
    const response = await apiAxios.get('/admin/dayly_user_stats', {
      params: { days, offset },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // Normalize API response: status -> success
    return {
      success: response.data.status === 'success',
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to fetch daily user stats',
    };
  }
};

/**
 * GET /admin/monthly_user_stats
 * Comprehensive monthly statistics (new users + transactions + deposits)
 * 🔥 MOST IMPORTANT - comprehensive statistics for dashboard
 */
export const getMonthlyUserStats = async (
  token: string,
  months: number = 11,
  offset: number = 0,
  order_desc: boolean = false
): Promise<ApiResponse<MonthlyUserStats[]>> => {
  try {
    const response = await apiAxios.get('/admin/monthly_user_stats', {
      params: { months, offset, order_desc },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // Normalize API response: status -> success
    return {
      success: response.data.status === 'success',
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to fetch monthly user stats',
    };
  }
};

/**
 * GET /admin/users_total
 * Total number of users
 */
export const getUsersTotal = async (
  token: string
): Promise<ApiResponse<UsersTotal>> => {
  try {
    const response = await apiAxios.get('/admin/users_total', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // Normalize API response: status -> success
    return {
      success: response.data.status === 'success',
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to fetch users total',
    };
  }
};

/**
 * GET /admin/mirror_statistics
 * Bot mirror statistics
 */
export const getMirrorStatistics = async (
  token: string,
  limit: number = 20,
  offset: number = 0,
  order_by: string = 'total_users'
): Promise<ApiResponse<MirrorStatistics>> => {
  try {
    const response = await apiAxios.get('/admin/mirror_statistics', {
      params: { limit, offset, order_by },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // Normalize API response: status -> success
    return {
      success: response.data.status === 'success',
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to fetch mirror statistics',
    };
  }
};

/**
 * Normalize a backend error envelope into an ApiResponse error string.
 *
 * Three shapes reach us, and the order matters:
 *  - `detail` — FastAPI's own `HTTPException` body (the 404 for an unknown
 *    mirror, the 400 for a malformed settlement period). Without this lookup
 *    those refusals surface as "Request failed with status code 404".
 *  - `message` — the repo's own `error_response` envelope.
 *  - `error` — the older admin routes' wording.
 *
 * Reading `detail` first is additive: the routes that predate it answer with
 * `message`/`error` and are unaffected.
 *
 * 🔴 `detail` is only taken when it is a STRING. FastAPI ships a 422 as
 * `{detail: [{...}, {...}]}` — an array of objects — and this function's callers
 * hand the result straight to `toast({title})`, which renders an object as
 * "[object Object]" or throws "Objects are not valid as a React child". The
 * declared `string` return cannot catch it because the parameter is `any`. Same
 * guard, same reason, as `ProfileReferals/helpers.ts::serverErrorMessage`.
 *
 * The fallback is last and deliberately dull: an admin deciding who may take
 * money must never be shown an invented reason.
 */
const extractError = (error: any, fallback: string): string => {
  const detail = error.response?.data?.detail;
  return (
    (typeof detail === "string" && detail) ||
    error.response?.data?.message ||
    error.response?.data?.error ||
    error.message ||
    fallback
  );
};

/**
 * GET /admin/user_lookup
 * Look up users by tg_username / email / user_id / username.
 */
export const userLookup = async (
  token: string,
  by: UserLookupField,
  q: string
): Promise<ApiResponse<UserLookupResult>> => {
  try {
    const response = await apiAxios.get('/admin/user_lookup', {
      params: { by, q },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return {
      success: response.data.status === 'success',
      data: response.data.data,
      error: response.data.status === 'error' ? response.data.message : undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: extractError(error, 'Failed to look up users'),
    };
  }
};

/**
 * GET /admin/user_state
 * Return a user's plans, summed remaining, balance and links.
 */
export const getUserState = async (
  token: string,
  userId: number
): Promise<ApiResponse<UserState>> => {
  try {
    const response = await apiAxios.get('/admin/user_state', {
      params: { user_id: userId },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return {
      success: response.data.status === 'success',
      data: response.data.data,
      error: response.data.status === 'error' ? response.data.message : undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: extractError(error, 'Failed to fetch user state'),
    };
  }
};

/**
 * POST /admin/credit_requests
 * Credit +amount requests to a user.
 */
export const creditRequests = async (
  token: string,
  userId: number,
  amount: number
): Promise<ApiResponse<CreditRequestsResult>> => {
  try {
    const response = await apiAxios.post(
      '/admin/credit_requests',
      { user_id: userId, amount },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return {
      success: response.data.status === 'success',
      data: response.data.data,
      error: response.data.status === 'error' ? response.data.message : undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: extractError(error, 'Failed to credit requests'),
    };
  }
};

/**
 * POST /admin/extend_subscription
 * Shift (delta) or set (absolute) a user's subscription date_end.
 */
export const extendSubscription = async (
  token: string,
  payload: ExtendSubscriptionPayload
): Promise<ApiResponse<UserState>> => {
  try {
    const response = await apiAxios.post('/admin/extend_subscription', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return {
      success: response.data.status === 'success',
      data: response.data.data,
      error: response.data.status === 'error' ? response.data.message : undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: extractError(error, 'Failed to extend subscription'),
    };
  }
};

/**
 * POST /admin/grant_plan
 * Grant a user a new plan from an existing product.
 */
export const grantPlan = async (
  token: string,
  userId: number,
  productId: number
): Promise<ApiResponse<UserState>> => {
  try {
    const response = await apiAxios.post(
      '/admin/grant_plan',
      { user_id: userId, product_id: productId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return {
      success: response.data.status === 'success',
      data: response.data.data,
      error: response.data.status === 'error' ? response.data.message : undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: extractError(error, 'Failed to grant plan'),
    };
  }
};

/**
 * GET /admin/products
 * List products for the grant-plan selector.
 */
export const getProducts = async (
  token: string
): Promise<ApiResponse<ProductsResult>> => {
  try {
    const response = await apiAxios.get('/admin/products', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return {
      success: response.data.status === 'success',
      data: response.data.data,
      error: response.data.status === 'error' ? response.data.message : undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: extractError(error, 'Failed to load products'),
    };
  }
};


// =====================
// Telegram Stars rate (spec 2026-08-26-stars-rate-and-admin-guard, US4)
// =====================

export interface StarsRate {
  /** Stars per one US dollar, after stored -> env -> built-in resolution. */
  rate: number;
  /**
   * Where the effective value came from. Shown to the admin because
   * "the rate is 100" is unfalsifiable without it — on 2026-09-02 production
   * turned out to be priced by an exception handler rather than by any setting.
   */
  source: 'stored' | 'env' | 'default';
  /** The raw stored value, or null when nothing is stored. */
  stored_value: string | null;
  default: number;
}

export const getStarsRate = async (
  token: string
): Promise<ApiResponse<StarsRate>> => {
  try {
    const response = await apiAxios.get('/admin/stars_rate', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      success: response.data.status === 'success',
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error:
        error.response?.data?.error || error.message || 'Failed to fetch Stars rate',
    };
  }
};

export const setStarsRate = async (
  token: string,
  rate: number
): Promise<ApiResponse<{ rate: number; previous: string | null; propagation: string }>> => {
  try {
    const response = await apiAxios.post(
      '/admin/stars_rate',
      { rate },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return {
      success: response.data.status === 'success',
      data: response.data.data,
      // The server refuses an invalid rate before storing; surface its wording
      // rather than inventing a client-side message that could disagree.
      error: response.data.status === 'success' ? undefined : response.data.message,
    };
  } catch (error: any) {
    return {
      success: false,
      error:
        error.response?.data?.error || error.message || 'Failed to save Stars rate',
    };
  }
};


// =====================
// Per-mirror Telegram Stars permission + settlement
// (spec 2026-09-02-mirror-stars-enablement, US1/US2/US3, plan steps 7 and 9)
// =====================

/** One mirror as the admin list reports it. NEVER carries `bot_api`. */
export interface MirrorStarsRow {
  /** The mirror's own id — what the toggle is addressed to. */
  id: number;
  bot_id: number | null;
  bot_username: string | null;
  /** The mirror's owner. Stars paid to this bot land in THEIR balance. */
  user_id: number | null;
  status: number;
  stars_enabled: boolean;
}

export interface MirrorStarsList {
  mirrors: MirrorStarsRow[];
  /** Rows matching the filters, not the page size — page without guessing. */
  total: number;
  limit: number;
  offset: number;
}

export interface MirrorStarsToggleResult {
  id: number;
  stars_enabled: boolean;
}

export interface MirrorStarsToggleResponse
  extends ApiResponse<MirrorStarsToggleResult> {
  /**
   * The API answered 404: the mirror is gone. Surfaced as its own flag rather
   * than left for the caller to string-match, because the right reaction is
   * different from any other failure — the list on screen is stale and has to
   * be reloaded, not retried.
   */
  notFound?: boolean;
}

export interface MirrorStarsQuery {
  status?: number;
  enabledOnly?: boolean;
}

/**
 * GET /admin/mirror_stars
 * Mirrors with their Stars permission, server-side paginated.
 *
 * A failure returns `success: false` with a reason and NO data — never an empty
 * page. An empty list reads as "no bots exist" and invites exactly the wrong
 * conclusion from an administrator deciding who may take money.
 */
export const getMirrorStars = async (
  token: string,
  limit: number = 50,
  offset: number = 0,
  query: MirrorStarsQuery = {}
): Promise<ApiResponse<MirrorStarsList>> => {
  try {
    const response = await apiAxios.get('/admin/mirror_stars', {
      params: {
        limit,
        offset,
        ...(query.status !== undefined ? { status: query.status } : {}),
        ...(query.enabledOnly ? { enabled_only: true } : {}),
      },
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      success: response.data.status === 'success',
      data: response.data.status === 'success' ? response.data.data : undefined,
      // The API reports a failed read in-band with HTTP 200
      // ({status: 'error', message}); a bare status-code check would read that
      // as a successful empty list.
      error:
        response.data.status === 'success' ? undefined : response.data.message,
    };
  } catch (error: any) {
    return {
      success: false,
      error: extractError(error, 'Failed to load the bot list'),
    };
  }
};

/**
 * POST /admin/mirror_stars
 * Switch Stars acceptance on or off for one mirror.
 *
 * Returns the state the server actually stored, so the caller can reconcile
 * rather than assume. A refusal arrives either as HTTP 4xx or in-band with
 * HTTP 200 and `status: 'error'` — both are failures here, and neither may be
 * reported to the admin as a saved setting.
 */
export const setMirrorStars = async (
  token: string,
  mirrorId: number,
  enabled: boolean
): Promise<MirrorStarsToggleResponse> => {
  try {
    const response = await apiAxios.post(
      '/admin/mirror_stars',
      { mirror_id: mirrorId, enabled },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return {
      success: response.data.status === 'success',
      data: response.data.status === 'success' ? response.data.data : undefined,
      error:
        response.data.status === 'success' ? undefined : response.data.message,
    };
  } catch (error: any) {
    return {
      success: false,
      notFound: error.response?.status === 404,
      error: extractError(error, 'Failed to save the Stars setting'),
    };
  }
};

/** One bot's take for the settlement period. */
export interface StarsSettlementRow {
  mirror_id: number | null;
  bot_id: number | null;
  bot_username: string | null;
  owner_user_id: number | null;
  stars_total: number;
  usd_total: number;
}

/** Stars taken by payments that name no bot. Always present, even at zero. */
export interface StarsSettlementTotals {
  stars_total: number;
  usd_total: number;
}

export interface StarsSettlement {
  period: { date_from: string; date_to: string };
  rows: StarsSettlementRow[];
  unattributed: StarsSettlementTotals;
}

/**
 * GET /admin/stars_settlement
 * Per-bot Stars totals for a period, plus the unattributed bucket.
 *
 * Both dates are optional ISO `YYYY-MM-DD` and inclusive; omitting them asks
 * the server for the current month. A malformed or inverted period is refused
 * with a 400 rather than silently widened — a plausible report for the wrong
 * dates is the failure mode this report exists to avoid.
 */
export const getStarsSettlement = async (
  token: string,
  dateFrom?: string,
  dateTo?: string
): Promise<ApiResponse<StarsSettlement>> => {
  try {
    const response = await apiAxios.get('/admin/stars_settlement', {
      params: {
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      },
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      success: response.data.status === 'success',
      data: response.data.status === 'success' ? response.data.data : undefined,
      error:
        response.data.status === 'success' ? undefined : response.data.message,
    };
  } catch (error: any) {
    return {
      success: false,
      error: extractError(error, 'Failed to load the settlement report'),
    };
  }
};
